// Concierge AI Service - Conversational place matching
// Uses Gemini ChatSession for multi-turn conversations

// 2026-04-24: AI läuft über Cloud Function `aiGateway`. GoogleGenerativeAI + ChatSession aus dem Shim.
import { GoogleGenerativeAI, type ChatSession } from './aiGatewayShim';
import type { Place, Realm } from '../types';
import type { ConciergeResponse, ConciergePlaceRecommendation } from '../types/concierge';

// Kein API-Key mehr im Frontend — Shim proxyt über Cloud Function.
const API_KEY = 'proxy';
const ai = new GoogleGenerativeAI(API_KEY);
const MODEL = 'gemini-2.5-flash';

// In-memory chat sessions (keyed by conversationId)
const chatSessions = new Map<string, ChatSession>();

// Store places for validation
const sessionPlaces = new Map<string, Place[]>();

/**
 * Build a city/location index for the AI to understand available locations
 */
function buildLocationIndex(places: Place[]): string {
  const locationMap = new Map<string, Set<string>>();

  places.forEach(p => {
    if (p.status !== 'approved') return;

    const city = p.city || 'Unbekannt';
    if (!locationMap.has(city)) {
      locationMap.set(city, new Set());
    }

    if (p.district) {
      locationMap.get(city)!.add(p.district);
    }
  });

  const lines: string[] = ['VERFUEGBARE STAEDTE UND STADTTEILE:'];
  locationMap.forEach((districts, city) => {
    const districtList = Array.from(districts).sort().join(', ');
    lines.push(`- ${city}: ${districtList || '(keine Stadtteile angegeben)'}`);
  });

  return lines.join('\n');
}

/**
 * Build detailed place context for the AI
 * Format: [Stadt/Stadtteil] Name - Beschreibung
 */
function buildPlaceContext(places: Place[], realm: Realm, limit = 60): string {
  const filteredPlaces = places
    .filter(p => p.status === 'approved')
    .filter(p => !p.realms || p.realms.includes(realm))
    .slice(0, limit);

  return filteredPlaces.map(p => {
    // Location is now FIRST and prominent
    const location = p.city
      ? (p.district ? `[${p.city}/${p.district}]` : `[${p.city}]`)
      : (p.district ? `[${p.district}]` : '[Ort unbekannt]');

    const category = realm === 'goodwork' && p.goodworkCategory
      ? p.goodworkCategory
      : p.category || '';

    const tags = realm === 'goodwork' && p.goodworkVibeTags?.length
      ? p.goodworkVibeTags.slice(0, 3).join(', ')
      : p.vibeTags?.slice(0, 3).join(', ') || '';

    const info = p.goodFactor || p.tagline || '';

    // More readable format with location FIRST
    // Use simple ID format without prefix so AI returns exact ID
    const parts = [
      `[${p.id}]`,
      location,
      `"${p.name}"`,
      category ? `(${category})` : '',
      tags ? `{${tags}}` : '',
      info ? `- ${info.substring(0, 120)}` : '',
    ].filter(Boolean);

    return parts.join(' ');
  }).join('\n');
}

/**
 * System prompt for the concierge - sachlicher Berater-Stil mit strikter Standort-Filterung
 */
function getSystemPrompt(realm: Realm, userName?: string): string {
  const realmContext = realm === 'goodwork'
    ? 'GoodWork Places - Arbeitsorte wie Coworking Spaces, Cafes zum Arbeiten, Bibliotheken'
    : 'GoodLife Places - Dritte Orte fuer Freizeit, Entspannung, Begegnung';

  return `Du bist der GoodPlace Concierge, ein sachlicher und hilfreicher Berater,
der Menschen hilft, passende Orte zu finden.

KONTEXT:
- Du hilfst bei: ${realmContext}
- ${userName ? `Der Nutzer heisst ${userName}.` : 'Sprich den Nutzer hoeflich mit "du" an.'}

DEIN STIL:
- Sachlich und direkt, aber hoeflich
- KEINE ueberschwenglichen Floskeln wie "Das klingt toll!", "Perfekt!", "Super Idee!"
- Sprich wie ein erfahrener Lokalkenner, der einem Freund hilft
- Duze den Nutzer

KRITISCHE REGELN ZUR STANDORT-FILTERUNG:
⚠️ WENN DER NUTZER EINE STADT ODER REGION NENNT (z.B. "in Muenchen", "in Berlin", "Maxvorstadt"):
- Empfehle AUSSCHLIESSLICH Orte aus dieser Stadt/Region!
- Pruefe bei JEDEM Ort die Angabe in eckigen Klammern [Stadt/Stadtteil]
- Wenn keine passenden Orte in dieser Stadt vorhanden sind, sage das EHRLICH
- Schlage NIEMALS Orte aus anderen Staedten vor, wenn eine Stadt genannt wurde!
- Bei Stadtteil-Anfragen (z.B. "Maxvorstadt"): Nur Orte aus diesem Stadtteil

BEISPIEL KORREKTES VERHALTEN:
User: "Ich suche einen ruhigen Ort in Muenchen"
→ Pruefe alle Orte: Nur die mit [Muenchen/...] empfehlen
→ Falls keiner passt: "In Muenchen habe ich leider keine passenden ruhigen Orte. Soll ich in anderen Staedten schauen?"

DEINE AUFGABE:
1. Verstehe genau, was der Nutzer sucht (Zweck, ORT, Stimmung, Anforderungen)
2. Wenn kein Ort genannt wurde: Frage nach der gewuenschten Stadt/Region
3. Filtere dann STRIKT nach diesem Standort
4. Empfehle passende Orte mit kurzer, sachlicher Begruendung
5. Erklaere WARUM ein Ort passt

WEITERE REGELN:
- Empfehle maximal 3-5 Orte pro Antwort
- Verwende NUR Orte aus der bereitgestellten Liste
- Wenn nichts passt, sag das ehrlich
- Beantworte Nachfragen zu bereits empfohlenen Orten konkret

ANTWORT-FORMAT:
Antworte mit natuerlichem Text. Wenn du Orte empfiehlst, fuege am Ende einen JSON-Block hinzu.
Der JSON-Block MUSS mit |||JSON||| beginnen und mit |||END||| enden.

WICHTIG fuer JSON:
- "placeId": Die exakte ID aus den ERSTEN eckigen Klammern der Datenbank (z.B. wenn dort "[abc123def]" steht, nutze "abc123def")
- "reason": IMMER eine ausfuehrliche, personalisierte Begruendung (2-3 Saetze), warum dieser Ort zur Anfrage passt

Beispiel:
"In der Maxvorstadt gibt es ein paar Optionen dafuer:

|||JSON|||
{
  "places": [
    {"placeId": "abc123def", "placeName": "Cafe XY", "reason": "Das Cafe hat eine besonders ruhige Ecke am Fenster, perfekt fuer konzentriertes Arbeiten. Der Filterkaffee ist hervorragend und das WLAN ist stabil."},
    {"placeId": "ghi789jkl", "placeName": "Ort Z", "reason": "Der hintere Bereich ist ueberraschend ruhig trotz der zentralen Lage. Die grossen Holztische bieten viel Platz fuer Laptop und Unterlagen."}
  ]
}
|||END|||"

Wenn du keine Orte empfiehlst (z.B. bei Rueckfragen), lass den JSON-Block weg.`;
}

/**
 * Start or resume a chat session
 */
export async function startConciergeChat(
  conversationId: string,
  places: Place[],
  realm: Realm,
  userName?: string
): Promise<boolean> {
  if (!ai) {
    console.error('Gemini AI not initialized - API key missing');
    return false;
  }

  try {
    const model = ai.getGenerativeModel({
      model: MODEL,
      generationConfig: {
        temperature: 0.5, // Lower temperature for more precise/factual responses
        topP: 0.85,
        maxOutputTokens: 8192, // Maximum for Gemini Flash to prevent truncation
      },
      systemInstruction: getSystemPrompt(realm, userName),
    });

    // Build location index and place context
    const locationIndex = buildLocationIndex(places);
    const placeContext = buildPlaceContext(places, realm);

    const contextMessage = `${locationIndex}

ORTE-DATENBANK (Format: [PlaceID] [Stadt/Stadtteil] "Name" (Kategorie) {Tags} - Info):
${placeContext}

WICHTIG:
- Achte bei jeder Empfehlung auf die Stadt in den eckigen Klammern!
- Verwende im JSON die exakte PlaceID aus den ersten eckigen Klammern (z.B. "abc123def456")`;

    // Start chat with context as first exchange
    const chat = model.startChat({
      history: [
        {
          role: 'user',
          parts: [{ text: contextMessage }],
        },
        {
          role: 'model',
          parts: [{ text: 'Ich habe die Orte-Datenbank geladen. In welcher Stadt oder Region suchst du einen Ort?' }],
        },
      ],
    });

    chatSessions.set(conversationId, chat);
    sessionPlaces.set(conversationId, places);
    return true;
  } catch (error) {
    console.error('Failed to start concierge chat:', error);
    return false;
  }
}

/**
 * Parse the hybrid response format (text + JSON block)
 */
function parseConciergeResponse(text: string): ConciergeResponse {
  const jsonMatch = text.match(/\|\|\|JSON\|\|\|([\s\S]*?)\|\|\|END\|\|\|/);

  let message = text;
  let places: ConciergePlaceRecommendation[] = [];
  let followUpQuestions: string[] = [];

  if (jsonMatch) {
    // Remove JSON block from message
    message = text.replace(/\|\|\|JSON\|\|\|[\s\S]*?\|\|\|END\|\|\|/, '').trim();

    try {
      const parsed = JSON.parse(jsonMatch[1].trim());
      places = (parsed.places || []).map((p: any) => {
        let cleanId = (p.placeId || '').toString();
        cleanId = cleanId.replace(/^ID:/i, '').replace(/^\[|\]$/g, '').trim();

        return {
          placeId: cleanId,
          placeName: p.placeName || '',
          reason: p.reason || '',
        };
      });
      followUpQuestions = parsed.followUpQuestions || [];
    } catch (e) {
      console.warn('Failed to parse concierge JSON:', e);
    }
  } else if (text.includes('|||JSON|||') && !text.includes('|||END|||')) {
    // Truncated response - remove incomplete JSON block from display
    message = text.replace(/\|\|\|JSON\|\|\|[\s\S]*$/, '').trim();
  }

  return { message, places, followUpQuestions };
}

/**
 * Validate that recommended places actually exist in our database
 */
function validatePlaceRecommendations(
  response: ConciergeResponse,
  conversationId: string
): ConciergeResponse {
  const places = sessionPlaces.get(conversationId);
  if (!places) return response;

  // Filter out any places that don't exist in our database
  const validatedPlaces = response.places.filter(rec => {
    const exists = places.some(p => p.id === rec.placeId);
    if (!exists) {
      console.warn(`Concierge recommended non-existent place: ${rec.placeId}`);
    }
    return exists;
  });

  return {
    ...response,
    places: validatedPlaces,
  };
}

/**
 * Send a message and get response
 */
export async function sendConciergeMessage(
  conversationId: string,
  userMessage: string
): Promise<ConciergeResponse | null> {
  const chat = chatSessions.get(conversationId);
  if (!chat) {
    console.error('No chat session found for:', conversationId);
    return null;
  }

  try {
    const result = await chat.sendMessage(userMessage);
    const responseText = result.response.text();

    // DEBUG: Check raw response and finish reason
    console.log('[Concierge] Response length:', responseText.length);
    console.log('[Concierge] Has JSON markers:', responseText.includes('|||JSON|||'), responseText.includes('|||END|||'));
    console.log('[Concierge] Finish reason:', result.response.candidates?.[0]?.finishReason);

    const parsed = parseConciergeResponse(responseText);
    console.log('[Concierge] Parsed places count:', parsed.places.length);

    const validated = validatePlaceRecommendations(parsed, conversationId);

    return validated;
  } catch (error) {
    console.error('Concierge message error:', error);

    // Handle overload errors with retry
    if (error instanceof Error && error.message.includes('503')) {
      console.log('Gemini overloaded, retrying in 2s...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        const result = await chat.sendMessage(userMessage);
        const responseText = result.response.text();
        const parsed = parseConciergeResponse(responseText);
        return validatePlaceRecommendations(parsed, conversationId);
      } catch (retryError) {
        console.error('Retry failed:', retryError);
        return null;
      }
    }

    return null;
  }
}

/**
 * Clear a chat session
 */
export function clearConciergeChat(conversationId: string): void {
  chatSessions.delete(conversationId);
  sessionPlaces.delete(conversationId);
}

/**
 * Check if a chat session exists
 */
export function hasConciergeChat(conversationId: string): boolean {
  return chatSessions.has(conversationId);
}
